import React, { useEffect, useCallback, useState } from 'react';
import { Book, Tag, Exemplar, TAG_CATEGORY_DISPLAY_NAMES } from '../types';
import { exemplarsApi, getThumbnailUrl } from '../services/api';
import { ImageGallery } from './ImageGallery';
import './BookDetailModal.css';

export interface BookDetailModalProps {
  /** Livro a ser exibido no modal */
  book: Book | null;
  /** Indica se o modal está aberto */
  isOpen: boolean;
  /** Callback chamado quando o modal é fechado */
  onClose: () => void;
  /** Exibir a seção de exemplares em estoque (dado interno — só admin) */
  showStock?: boolean;
}

/**
 * Componente BookDetailModal
 * 
 * Exibe um modal com detalhes completos do livro incluindo:
 * - Título, descrição e ano de publicação
 * - Galeria de imagens com navegação
 * - Tags organizadas por categoria
 * - Status de disponibilidade física
 * - Botão para fechar e suporte a tecla Escape
 * 
 * Valida: Requisitos 4.3
 */
export function BookDetailModal({
  book,
  isOpen,
  onClose,
  showStock = false,
}: BookDetailModalProps): React.ReactElement | null {
  // Exemplares de estoque vinculados a este projeto (busca só para admin —
  // a API bloqueia viewers com 403 de qualquer forma)
  const [stockExemplars, setStockExemplars] = useState<Exemplar[]>([]);

  useEffect(() => {
    if (!isOpen || !book || !showStock) return;
    let cancelled = false;
    // Zera a lista do projeto anterior para não renderizar estoque do livro
    // errado enquanto o fetch do novo não chega
    setStockExemplars([]);
    exemplarsApi
      .getAll({ bookId: book.id })
      .then((data) => {
        if (!cancelled) setStockExemplars(data);
      })
      .catch(() => {
        // Falha ao carregar estoque não bloqueia o modal
        if (!cancelled) setStockExemplars([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, book, showStock]);

  // Handle keyboard navigation for Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || !book) return;

      if (event.key === 'Escape') {
        onClose();
      }
      // Arrow key navigation is now handled by ImageGallery component
    },
    [isOpen, book, onClose]
  );

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open or no book
  if (!isOpen || !book) return null;

  const images = book.images || [];

  // Group tags by category
  const tagsByCategory = groupTagsByCategory(book.tags || []);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="book-detail-modal__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-detail-title"
    >
      <div className="book-detail-modal">
        {/* Mobile Header com título e botão fechar */}
        <div className="book-detail-modal__mobile-header">
          <h2 className="book-detail-modal__mobile-title">{book.title}</h2>
          <button
            type="button"
            className="book-detail-modal__close-button book-detail-modal__close-button--mobile"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        {/* Close Button (desktop) */}
        <button
          type="button"
          className="book-detail-modal__close-button book-detail-modal__close-button--desktop"
          onClick={onClose}
          aria-label="Fechar modal"
        >
          ✕
        </button>

        <div className="book-detail-modal__content">
          {/* Image Gallery Section - Using reusable ImageGallery component */}
          <div className="book-detail-modal__gallery">
            <ImageGallery
              images={images}
              bookTitle={book.title}
              enableZoom={true}
              showDots={true}
              showThumbnails={true}
              showArrows={true}
            />
          </div>

          {/* Book Details Section */}
          <div className="book-detail-modal__details">
            {/* Title */}
            <h2 id="book-detail-title" className="book-detail-modal__title">
              {book.title}
            </h2>

            {/* Publication Year */}
            {book.publicationYear && (
              <span className="book-detail-modal__year">
                Ano de publicação: {book.publicationYear}
              </span>
            )}

            {/* Availability Badge */}
            <div className="book-detail-modal__badges">
              <div
                className={`book-detail-modal__availability ${
                  book.physicallyAvailable
                    ? 'book-detail-modal__availability--available'
                    : 'book-detail-modal__availability--unavailable'
                }`}
              >
                <span className="book-detail-modal__availability-icon">
                  {book.physicallyAvailable ? '✓' : '✗'}
                </span>
                <span className="book-detail-modal__availability-text">
                  {book.physicallyAvailable
                    ? 'Disponível fisicamente'
                    : 'Não disponível fisicamente'}
                </span>
              </div>

              {book.isAwarded && (
                <div className="book-detail-modal__awarded">
                  <span className="book-detail-modal__awarded-icon">🏆</span>
                  <span className="book-detail-modal__awarded-text">Projeto Premiado</span>
                </div>
              )}

              {book.boxType && (
                <div className="book-detail-modal__awarded">
                  <span className="book-detail-modal__awarded-icon">📦</span>
                  <span className="book-detail-modal__awarded-text">{getBoxTypeLabel(book.boxType)}</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="book-detail-modal__description-section">
              <h3 className="book-detail-modal__section-title">Descrição</h3>
              <p className="book-detail-modal__description">{book.description}</p>
            </div>

            {/* Exemplares em estoque (Mauá) — dado interno, apenas admin */}
            {showStock && stockExemplars.length > 0 && (
              <div className="book-detail-modal__stock-section">
                <h3 className="book-detail-modal__section-title">📦 Exemplares em estoque</h3>
                <div className="book-detail-modal__stock-list">
                  {stockExemplars.map((exemplar) => (
                    <div key={exemplar.id} className="book-detail-modal__stock-item">
                      <span className="book-detail-modal__stock-photo">
                        {exemplar.photoFilepath ? (
                          <img
                            src={getThumbnailUrl(exemplar.photoFilepath)}
                            alt={exemplar.title}
                            loading="lazy"
                          />
                        ) : (
                          '📦'
                        )}
                      </span>
                      <span className="book-detail-modal__stock-info">
                        <span className="book-detail-modal__stock-op">OP {exemplar.opNumber}</span>
                        <span className="book-detail-modal__stock-quantity">
                          {exemplar.quantity} {exemplar.quantity === 1 ? 'peça' : 'peças'} · {exemplar.location}
                          {exemplar.locationDetail ? ` — ${exemplar.locationDetail}` : ''}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tags by Category */}
            {tagsByCategory.length > 0 && (
              <div className="book-detail-modal__tags-section">
                <h3 className="book-detail-modal__section-title">Categorias</h3>
                <div className="book-detail-modal__tags-container">
                  {tagsByCategory.map((categoryGroup) => (
                    <div
                      key={categoryGroup.categoryName}
                      className="book-detail-modal__tag-category"
                    >
                      <span className="book-detail-modal__tag-category-name">
                        {categoryGroup.displayName}:
                      </span>
                      <div className="book-detail-modal__tag-list">
                        {categoryGroup.tags.map((tag) => (
                          <span key={tag.id} className="book-detail-modal__tag">
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Returns the display label for a box type
 */
function getBoxTypeLabel(boxType: string): string {
  const labels: Record<string, string> = {
    rigida: 'Caixa Rígida',
    semirrigida: 'Caixa Semirrígida',
    hibrida: 'Caixa Híbrida (Rígida e Semirrígida)',
  };
  return labels[boxType] || boxType;
}

/**
 * Groups tags by their category
 */
interface TagCategoryGroup {
  categoryName: string;
  displayName: string;
  tags: Tag[];
}

function groupTagsByCategory(tags: Tag[]): TagCategoryGroup[] {
  const categoryMap = new Map<string, TagCategoryGroup>();

  // Define the order of categories
  const categoryOrder = [
    'process',
    'paper',
    'finishing',
    'technology',
    'enhancement',
    'work_type',
  ];

  tags.forEach((tag) => {
    const categoryName = tag.category?.name || 'other';
    const displayName =
      tag.category?.displayName ||
      TAG_CATEGORY_DISPLAY_NAMES[categoryName] ||
      'Outros';

    if (!categoryMap.has(categoryName)) {
      categoryMap.set(categoryName, {
        categoryName,
        displayName,
        tags: [],
      });
    }

    categoryMap.get(categoryName)!.tags.push(tag);
  });

  // Sort by predefined order
  const result: TagCategoryGroup[] = [];
  categoryOrder.forEach((categoryName) => {
    const group = categoryMap.get(categoryName);
    if (group) {
      result.push(group);
      categoryMap.delete(categoryName);
    }
  });

  // Add any remaining categories
  categoryMap.forEach((group) => {
    result.push(group);
  });

  return result;
}

export default BookDetailModal;
